// eslint-disable-next-line sonarjs/todo-tag
// TODO: Monacoに型がつかないため、anyを許している

import { FunctionOutlined } from '@ant-design/icons'
import Editor, { useMonaco } from '@monaco-editor/react'
import { abbreviateText, type RecursivePartial } from '@salescore/buff-common'
import type { ViewConfigSheet } from '@salescore/core'
import { Alert, Col, Dropdown, Form, Menu, Row } from 'antd'
import { Array, pipe } from 'effect'
import { isNotNullish } from 'effect/Predicate'
// @monaco-editor/reactがexportするtypeがなぜかanyになるので、自前で同等の型を付与したかったが、compileでエラーになるのでボツ
// import { editor, Range } from 'monaco-editor'
import { t } from 'i18next'
import { type ReactElement, useEffect, useMemo, useRef } from 'react'
import { format } from 'sql-formatter'

import { NodePropertyPickerContent } from '../../../components/view_ui/ViewUISheetWithNavigation/SheetNavigation/common/NodePropertyPickerContent'
import { useGetModel } from '../../../recoil/view/selectors/connectionsSelector'
import { flatNodes } from '../../../state/nodeUtility'
import { MonacoEditorNavigationButton, MonacoEditorNavigationHeader } from './ConditionalEffectExpressionEditor'

const POSTGRES_SQL_STATEMENTS = [`SELECT`, `FROM`, `JOIN`, `WHERE`, `AND`, `LIMIT`, `OFFSET`]

const POSTGRES_SQL_FUNCTIONS = [
  { label: t(`カウント`), value: `COUNT(*)` },
  { label: t(`合計`), value: `SUM()` },
  { label: t(`平均`), value: `AVG()` },
  { label: t(`割合`), value: `COUNT(CASE WHEN flag = 1 THEN 1 ELSE NULL END)::NUMERIC / NULLIF(COUNT(*), 0)` },
  { label: t(`最小`), value: `MIN()` },
  { label: t(`最大`), value: `MAX()` },
]

// eslint-disable-next-line sonarjs/prefer-read-only-props
export function MeasureCustomSqlEditor({ sheetConfig }: { sheetConfig: RecursivePartial<ViewConfigSheet> }) {
  const getModel = useGetModel()
  const { propertiesWithNode, modelsWithNode } = useMemo(() => {
    const { tree } = sheetConfig
    const nodes = flatNodes(tree)
    const modelsWithNode = pipe(
      nodes,
      Array.flatMapNullishOr((node) => {
        const model = getModel(node.modelName)
        if (model === undefined) {
          return
        }
        return {
          node,
          model,
        }
      }),
    )
    const propertiesWithNode = modelsWithNode.flatMap((x) =>
      x.model.properties.map((property) => ({
        node: x.node,
        model: x.model,
        property,
      })),
    )
    return {
      propertiesWithNode,
      modelsWithNode,
    }
  }, [getModel, sheetConfig])

  const monaco = useMonaco()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const editorReference = useRef<any>(null)
  // const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null)

  useEffect(() => {
    if (monaco !== null) {
      // eslint-disable-next-line sonarjs/todo-tag
      // TODO: suggestion
      const suggestions = [
        ...POSTGRES_SQL_STATEMENTS.map((command) => ({
          label: command,
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: `${command} `,
        })),
        ...POSTGRES_SQL_FUNCTIONS.map((command) => ({
          label: command.value,
          detail: command.label,
          kind: monaco.languages.CompletionItemKind.Function,
          insertText: `${command.value} `,
        })),
        ...pipe(
          modelsWithNode,
          Array.dedupeWith((a, b) => a.node.name === b.node.name),
          Array.map((modelWithNode) => ({
            label: modelWithNode.model.label,
            detail: modelWithNode.model.label,
            kind: monaco.languages.CompletionItemKind.Field,
            insertText: `"${modelWithNode.node.name}" `,
          })),
        ),
        ...pipe(
          propertiesWithNode,
          Array.dedupeWith((a, b) => `${a.node.name}.${a.property.name}` === `${b.node.name}.${b.property.name}`),
          Array.map((propertyWithNode) => ({
            // label: propertyWithNode.column_name,
            label: `${propertyWithNode.node.name}.${propertyWithNode.property.name}`,
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            detail: `${propertyWithNode.property.label ?? ''}(${
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              propertyWithNode.model.label ?? ''
            })`,
            kind: monaco.languages.CompletionItemKind.Field,
            insertText: `"${propertyWithNode.node.name}"."${propertyWithNode.property.name}" `,
          })),
        ),
      ]
      monaco.languages.registerCompletionItemProvider('sql', {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        provideCompletionItems(model: any, position: any) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
          const word = model.getWordUntilPosition(position)
          return {
            suggestions: pipe(
              suggestions,
              Array.dedupeWith((a, b) => a.insertText === b.insertText),
              Array.map((x) => ({
                ...x,
                range: {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
                  startLineNumber: position.lineNumber,
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
                  endLineNumber: position.lineNumber,
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
                  startColumn: word.startColumn,
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
                  endColumn: word.endColumn,
                },
              })),
            ),
          }
        },
      })
    }
  }, [monaco])

  const insert = (text: string) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const editor = editorReference.current
    if (editor === null) {
      return
    }
    // // https://microsoft.github.io/monaco-editor/api/interfaces/monaco.editor.IEditor.html#getPosition
    // const position = editor.getPosition()
    // // https://microsoft.github.io/monaco-editor/api/interfaces/monaco.editor.ITextModel.html#getValue
    // const sql = editor.getModel()?.getValue()
    // if (isNull(sql) || isNull(position)) {
    //   return
    // }
    // form.setFieldsValue({ sql: format(sql, { language: 'postgresql' }) })

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
    const selection = editor.getSelection()
    // https://qiita.com/lumis/items/311b8c39d61312957195#%E3%82%A8%E3%83%87%E3%82%A3%E3%82%BF%E3%81%A7%E7%B7%A8%E9%9B%86%E3%81%99%E3%82%8B
    // editor.executeEdits(``, [{ range: selection ?? new Range(1, 1, 1, 1), text }])
    if (isNotNullish(selection)) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
      editor.executeEdits(``, [{ range: selection, text }])
    }
  }

  return (
    <div className="overflow-hidden">
      <Alert
        showIcon
        className="mb-4 mr-6"
        type="info"
        description={<div>{t(`SQLを使って集計をすることができます。`)}</div>}
      />
      <Row>
        <Col span={14}>
          <MonacoEditorNavigationHeader title={t(`SQLエディタ`)}>
            <>
              <SqlFunctionDropdown
                onFinish={(text) => {
                  insert(text)
                }}
              >
                <MonacoEditorNavigationButton icon={<FunctionOutlined />}>{t(`関数`)}</MonacoEditorNavigationButton>
              </SqlFunctionDropdown>
              <MonacoEditorNavigationButton
                icon={<FunctionOutlined />}
                onClick={() => {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  const editor = editorReference.current
                  if (editor === null) {
                    return
                  }
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-type-assertion
                  const sql = editor.getModel()?.getValue() as string | null
                  if (sql === null) {
                    return
                  }
                  const formatted = format(sql, { language: 'postgresql' })
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-member-access
                  editor.getModel()?.setValue(formatted)
                }}
              >
                {t(`フォーマット`)}
              </MonacoEditorNavigationButton>
            </>
          </MonacoEditorNavigationHeader>
          <Form.Item name="sql" rules={[{ required: true, message: t(`SQLを入力してください`) }]}>
            <Editor
              height={500}
              theme="vs-dark"
              defaultLanguage="sql"
              defaultValue={`COUNT(*)`}
              onMount={(editor, monaco) => {
                editorReference.current = editor
              }}
            />
          </Form.Item>
        </Col>
        <Col span={10}>
          <div className="pl-8">
            <NodePropertyPickerContent
              showInternalName
              propertiesWithNode={propertiesWithNode}
              onFinish={(nodePropertyName) => {
                // onFinish(nodePropertyName)
                insert(`"${nodePropertyName.nodeName}"."${nodePropertyName.propertyName}"`)
              }}
            />
          </div>
        </Col>
      </Row>
    </div>
  )
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
function SqlFunctionDropdown({ onFinish, children }: { onFinish: (text: string) => void; children: ReactElement }) {
  const menu = (
    <Menu
      items={POSTGRES_SQL_FUNCTIONS.map(({ value, label }) => ({
        key: value,
        label: (
          <div>
            {label}
            <span className="ml-2 opacity-60">{abbreviateText(value, 15)}</span>
          </div>
        ),
        onClick: () => {
          onFinish(value)
        },
      }))}
    />
  )

  return (
    // eslint-disable-next-line sonarjs/deprecation
    <Dropdown overlay={menu} trigger={['click']}>
      {children}
    </Dropdown>
  )
}
